import { useDoubleConfirm } from '../contexts/AlertContext';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { useAlert, useConfirm } from '../contexts/AlertContext';
import TimePicker from '../components/TimePicker';
import IntervalPicker from '../components/IntervalPicker';

// Gyors görgetés a szerkesztéshez
const scrollToElementFast = (element) => {
  if (!element) return;

  const start = window.scrollY;
  const target = element.getBoundingClientRect().top + window.scrollY;
  const distance = target - start;
  const duration = 180;
  const startTime = performance.now();

  const animate = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    window.scrollTo(0, start + distance * easedProgress);
    if (progress < 1) requestAnimationFrame(animate);
  };

  requestAnimationFrame(animate);
};

// Fő admin felület: statisztikák, a napok/zárások állapotának, a beállítások mentése és a foglalások kezelése.
export default function AdminDashboard() {
  const { user, setSiteName, setSiteTitle } = useAuth();
  const showAlert = useAlert();
  const confirm = useConfirm();
    const doubleConfirm = useDoubleConfirm();
  
  // Állapotok a dashboardhoz:
  // - stat: összesítő adatok
  // - allBookings: az összes foglalás a backendből
  // - blockedDates/customDays: szünetek és egyedi nyitvatartási napok
  // - viewMode: az aktuálisan megjelenített fül (foglalások, munkaidő, stb.)
  // - manualBooking: telefonos foglalás létrehozásához szükséges adatok
  const [stats, setStats] = useState({ todayBookings: 0, monthlyRevenue: 0, customers: 0, servicesCount: 0 });
  const [allBookings, setAllBookings] = useState([]); 
  const [blockedDates, setBlockedDates] = useState([]);
  const [customDays, setCustomDays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [viewMode, setViewMode] = useState('bookings'); 
  const [selectedAdminDate, setSelectedAdminDate] = useState(new Date().toISOString().split('T')[0]);
  const [workTime, setWorkTime] = useState({ start: "10:00", end: "14:00" });
  const [isTabsMenuOpen, setIsTabsMenuOpen] = useState(false);
  const isBookingsTab = viewMode === 'bookings';
  const [manualBooking, setManualBooking] = useState({ name: '', phone: '', time: '08:00', serviceId: '', withWashing: false });
  const [isCreatingManualBooking, setIsCreatingManualBooking] = useState(false);
  
  const [washingPrice, setWashingPrice] = useState(500);
  const [washingEnabled, setWashingEnabled] = useState(true);
  const [openingHours, setOpeningHours] = useState({ open: "08:00", close: "16:00" });
  const [slotInterval, setSlotInterval] = useState(20);
  const [siteName, setSiteNameValue] = useState('Fodrász');
  const [siteTitle, setSiteTitleValue] = useState('Fodrász');
  const [isSaving, setIsSaving] = useState(false);

  const [servicesList, setServicesList] = useState([]);
  const [newService, setNewService] = useState({ name: '', price: '', duration: 15, description: '' });
  const servicesSectionRef = useRef(null);
  const tabsMenuRef = useRef(null);
  
  const [editingServiceId, setEditingServiceId] = useState(null);

  // Adatbetöltés a felhasználó bejelentkezése után.
  useEffect(() => {
    if (user && user.role === 'Fodrász') {
      fetchAdminData();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!tabsMenuRef.current) return;
      if (!tabsMenuRef.current.contains(event.target)) {
        setIsTabsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return <Navigate to="/bejelentkezes" replace />;
  if (user.role !== 'Fodrász') return <Navigate to="/profil" replace />;

  // A backendből párhuzamosan kérjük le az adatokat
  const fetchAdminData = async () => {
    try {
      const [statsRes, blockedRes, customRes, allBookingsRes, settingsRes, servicesRes] = await Promise.all([
        axios.get(`/api/admin/stats`),
        axios.get(`/api/blocked-dates`),
        axios.get(`/api/custom-days`),
        axios.get(`/api/admin/all-bookings`),
        axios.get(`/api/settings`),
        axios.get(`/api/services`)
      ]);
      setStats(statsRes.data);
      setBlockedDates(blockedRes.data);
      setCustomDays(customRes.data);
      setAllBookings(allBookingsRes.data);
      if (settingsRes.data.washing_price) setWashingPrice(parseInt(settingsRes.data.washing_price));
      setWashingEnabled(settingsRes.data.washing_enabled !== '0');
      setSiteNameValue(settingsRes.data.site_name || 'Fodrász');
      setSiteTitleValue(settingsRes.data.site_title || 'Fodrász');
      setOpeningHours({
        open: settingsRes.data.open_time || "08:00",
        close: settingsRes.data.close_time || "16:00"
      });
      const savedSlotInterval = Number(settingsRes.data.slot_interval);
      setSlotInterval(Number.isInteger(savedSlotInterval) && savedSlotInterval >= 5 && savedSlotInterval <= 60 && savedSlotInterval % 5 === 0 ? savedSlotInterval : 20);
      setServicesList(servicesRes.data);
    } catch (err) {
      console.error("Hiba az adatok betöltésekor:", err);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
        await Promise.all([
          axios.post(`/api/settings/hours`, {
            openTime: openingHours.open,
            closeTime: openingHours.close
          }),
          axios.post(`/api/settings`, {
            siteName,
            siteTitle,
            washingPrice,
            washingEnabled,
            slotInterval
          })
        ]);
        setSiteName(siteName.trim());
        setSiteTitle(siteTitle.trim());
        showAlert("Beállítások mentve!");
        fetchAdminData();
    } catch (error) {
        console.error("Hiba:", error);
        showAlert(error.response?.data?.error || "Hiba történt a mentés során!");
    }
    setIsSaving(false);
  };

  const handleSaveService = async () => {
    if (!newService.name || !newService.price || !newService.duration) {
      return showAlert("A Név, Ár és Időtartam mezők kitöltése kötelező!");
    }
    try {
        if (editingServiceId) {
            await axios.put(`/api/services/${editingServiceId}`, newService);
            showAlert("Szolgáltatás sikeresen frissítve!");
        } else {
            await axios.post(`/api/services`, newService);
            showAlert("Szolgáltatás sikeresen hozzáadva!");
        }
        
        setEditingServiceId(null);
        setNewService({ name: '', price: '', duration: 15, description: '' });
        fetchAdminData(); 
    } catch (error) {
        console.error("Hiba a mentés során:", error);
        showAlert("Hiba történt a szolgáltatás mentése során!");
    }
  };

  const handleEditClick = (srv) => {
    setEditingServiceId(srv.id);
    setNewService({
        name: srv.name,
        price: srv.price,
        duration: srv.duration || 30,
        description: srv.description || '',
    });
    scrollToElementFast(servicesSectionRef.current);
  };

  const handleCancelEdit = () => {
    setEditingServiceId(null);
    setNewService({ name: '', price: '', duration: 15, description: '' });
  };

  const handleDeleteService = async (id) => {
    if (await doubleConfirm("Biztosan törölni szeretnéd ezt a szolgáltatást?", "A szolgáltatás véglegesen törlődik. Biztosan folytatod?")) {
        try {
            await axios.delete(`/api/services/${id}`);
            if (editingServiceId === id) handleCancelEdit();
            fetchAdminData(); 
        } catch (error) {
            console.error("Hiba a törléskor:", error);
            showAlert("Hiba történt a törlés során!");
        }
    }
  };

  // A naptár napjaira kattintva a rendszer a kiválasztott mód szerint hajtja végre
  // Egyedi munkaidő hozzáadása, zárva/nyitva állapot váltása
  // A szünet nap eltávolítva + dupla megerősítés
  const handleDayClick = async (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isClosed = blockedDates.some(d => d.date === dStr);
    const isCustom = customDays.some(d => d.date === dStr);

    setSelectedAdminDate(dStr);

    try {
      if (viewMode === 'schedule') {
        if (isClosed) return showAlert("SZÜNET napra nem állítható munkaidő!");
        if (isCustom) {
          if (!await doubleConfirm("Biztosan törölni szeretnéd ennek a napnak az egyedi munkaidejét?", "Az egyedi munkaidő véglegesen törlődik. Biztosan folytatod?")) return;
          await axios.delete(`/api/custom-days/${dStr}`);
        } else {
          await axios.post(`/api/custom-days`, {
            date: dStr,
            start: workTime.start,
            end: workTime.end
          });
        }
      } else if (viewMode === 'closed') {
        if (isClosed) {
          if (!await doubleConfirm("Biztosan fel szeretnéd oldani ezt a szabadnapot?", "A szabadnap beállítása véglegesen törlődik. Biztosan folytatod?")) return;
          await axios.delete(`/api/blocked-dates/${dStr}`);
        } else {
          if (isCustom) {
            if (!await doubleConfirm("Ez a nap egyedi munkaidőt tartalmaz. Biztosan törölni szeretnéd?", "Az egyedi munkaidő véglegesen törlődik. Biztosan folytatod?")) return;
            await axios.delete(`/api/custom-days/${dStr}`).catch(() => {});
          }
          await axios.post(`/api/blocked-dates`, { date: dStr });
        }
      }
      fetchAdminData();
    } catch (err) {
      console.error(err);
      showAlert("Hiba történt a művelet során!");
    }
  };

  const handleDeleteBooking = async (id) => {
    if (await doubleConfirm("Biztosan törölni szeretnéd ezt a foglalást a naptárból?", "A foglalás véglegesen törlődik. Biztosan folytatod?")) {
      try {
        await axios.delete(`/api/bookings/${id}`);
        fetchAdminData(); 
      } catch (e) { 
        showAlert("Hiba a foglalás törlésekor!"); 
      }
    }
  };

  const handleCompleteBooking = async (id) => {
    if (await confirm("Sikeresen lezajlott a szolgáltatás? Ezzel a foglalás Kész állapotba kerül és hozzáadódik a bevételhez.")) {
      try {
        await axios.put(`/api/bookings/${id}/status`, { status: 'Kész' });
        fetchAdminData(); 
      } catch (e) {
        showAlert("Hiba a státusz frissítésekor!");
      }
    }
  };

  const handleNoShowBooking = async (id) => {
    if (await confirm("Biztosan megjelölöd ezt a foglalást úgy, hogy a vendég nem jött el?")) {
      try {
        await axios.put(`/api/bookings/${id}/status`, { status: 'Nem jött' });
        fetchAdminData();
      } catch (e) {
        showAlert("Hiba a státusz frissítésekor!");
      }
    }
  };

  // A telefonos foglalás nem a publikus felületből jön, hanem admin felületen történik
  // Külön feldolgozása van a vendég adatoknak és az időpontoknak.
  const handleCreateManualBooking = async (event) => {
    event.preventDefault();
    if (!manualBooking.serviceId) return showAlert("Válassz szolgáltatást!");

    setIsCreatingManualBooking(true);
    try {
      await axios.post(`/api/admin/manual-bookings`, {
        date: selectedAdminDate,
        time: manualBooking.time,
        serviceId: manualBooking.serviceId,
        guestName: manualBooking.name,
        guestPhone: manualBooking.phone,
        withWashing: manualBooking.withWashing
      });
      showAlert("Telefonos foglalás sikeresen rögzítve!");
      setManualBooking({ name: '', phone: '', time: '08:00', serviceId: '', withWashing: false });
      setViewMode('bookings');
      fetchAdminData();
    } catch (error) {
      showAlert(error.response?.data?.message || "Hiba történt a telefonos foglalás mentésekor!");
    } finally {
      setIsCreatingManualBooking(false);
    }
  };
//hónapok és napok a naptárban
  const monthNames = ["Január", "Február", "Március", "Április", "Május", "Június", "Július", "Augusztus", "Szeptember", "Október", "November", "December"];
  const dayNames = ["Hét", "Ked", "Sze", "Csü", "Pén", "Szo", "Vas"];
  const todayStr = new Date().toISOString().split('T')[0];

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const offset = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

  // A kiválasztott nap foglalásainak szűrése és csoportosítása.
  // A délelőtti és délutáni időszakok külön kezelése a UI-nál fontos,
  // hogy a foglalásokat olvashatóan lehessen megjeleníteni.
  const dailyBookings = allBookings
    .filter(b => b.date === selectedAdminDate)
    .sort((a,b) => a.time.localeCompare(b.time));

  const morningApts = dailyBookings.filter(a => parseInt(a.time.split(':')[0]) < 12);
  const afternoonApts = dailyBookings.filter(a => parseInt(a.time.split(':')[0]) >= 12);

  // A telefonos foglalásnál ellenőrzi, hogy a kiválasztott időpont tényleg szabad-e.
  // Figyelembe veszi a saját hozzáadott szolgáltatás időtartamát és a nyitvatartási időt
  // Hétvégi és egyedi igazításokat
  const getManualAvailableSlots = () => {
    const service = servicesList.find(item => String(item.id) === String(manualBooking.serviceId));
    if (!service || !selectedAdminDate) return [];

    const dateObj = new Date(`${selectedAdminDate}T00:00:00`);
    const customDay = customDays.find(day => day.date === selectedAdminDate);
    if ((dateObj.getDay() === 0 || dateObj.getDay() === 6) && !customDay) return [];

    const startTime = customDay?.start_time || openingHours.open;
    const endTime = customDay?.end_time || openingHours.close;
    const serviceDuration = Number(service.duration) || 30;
    const startMinutes = Number(startTime.split(':')[0]) * 60 + Number(startTime.split(':')[1]);
    const endMinutes = Number(endTime.split(':')[0]) * 60 + Number(endTime.split(':')[1]);
    const bookings = allBookings
      .filter(booking => booking.date === selectedAdminDate && booking.status !== 'Lemondva')
      .map(booking => {
        const [hours, minutes] = booking.time.split(':').map(Number);
        const [endHours, endMinutes] = (booking.endTime || '').split(':').map(Number);
        const start = hours * 60 + minutes;
        const end = Number.isNaN(endHours) || Number.isNaN(endMinutes) ? start + slotInterval : endHours * 60 + endMinutes;
        return { start, end };
      });

    return Array.from({ length: Math.max(0, Math.floor((endMinutes - startMinutes - serviceDuration) / slotInterval) + 1) }, (_, index) => {
      const minutes = startMinutes + index * slotInterval;
      const overlaps = bookings.some(booking => minutes < booking.end && minutes + serviceDuration > booking.start);
      return overlaps ? null : `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    }).filter(Boolean);
  };

  const manualAvailableTimes = getManualAvailableSlots();
  const manualMorningSlots = manualAvailableTimes.filter(time => Number(time.split(':')[0]) < 12);
  const manualAfternoonSlots = manualAvailableTimes.filter(time => Number(time.split(':')[0]) >= 12);

  // Statisztika kártyák (mai foglalások és szolgáltatások száma)
  // Naptár és foglalási listák
  // admin menü fülek (beállítások, szolgáltatások, munkaidő, stb.)
  
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8 font-sans italic bg-transparent min-h-screen transition-colors duration-300">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-12 mb-8 sm:mb-12 text-white text-center font-sans not-italic">
        <StatCard title="Mai Vendég" value={stats.todayBookings} color="bg-blue-600 dark:bg-blue-700" />
        <StatCard title="Szolgáltatások" value={stats.servicesCount} color="bg-purple-500 dark:bg-purple-600" />
      </div>
  {/*bal oldali*/}
      <div className="grid lg:grid-cols-5 gap-6 sm:gap-8">
        {isBookingsTab && (
          <div className="lg:col-span-2 bg-white/70 dark:bg-slate-900/60 rounded-xl sm:rounded-[1rem] p-5 sm:p-8 shadow-sm border border-gray-900 dark:border-gray-300 transition-colors duration-300">
            <div className="admin-bookings-scroll admin-bookings-content max-h-[calc(100vh-2rem)] overflow-y-auto lg:max-h-[42rem] xl:max-h-[48rem]">
              <div className="flex justify-between items-center mb-6 border-b dark:border-gray-700 pb-4">
              <h2 className="text-lg sm:text-xl font-black uppercase tracking-tighter dark:text-white">{selectedAdminDate.replace(/-/g, '. ')}</h2>
              {selectedAdminDate === todayStr && <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 text-[8px] font-black px-2 py-1 rounded-full uppercase">Ma</span>}
              </div>
              <div className="space-y-6 sm:space-y-8">
              <section>
                <p className="text-[10px] font-black text-blue-500 dark:text-blue-400 uppercase mb-3 tracking-widest">Délelőtt ({morningApts.length})</p>
                {morningApts.length > 0 ? morningApts.map((apt, i) => <BookingRow key={i} apt={apt} onDelete={handleDeleteBooking} onComplete={handleCompleteBooking} onNoShow={handleNoShowBooking} />) : <EmptyScheduleState label="A délelőtt jelenleg szabad" />}
              </section>
              <section>
                <p className="text-[10px] font-black text-orange-500 dark:text-orange-400 uppercase mb-3 tracking-widest">Délután ({afternoonApts.length})</p>
                {afternoonApts.length > 0 ? afternoonApts.map((apt, i) => <BookingRow key={i} apt={apt} onDelete={handleDeleteBooking} onComplete={handleCompleteBooking} onNoShow={handleNoShowBooking} />) : <EmptyScheduleState label="A délután jelenleg szabad" />}
              </section>
              </div>
            </div>
          </div>
        )}
      {/*jobb oldali*/ }
        <div className={`${isBookingsTab ? 'lg:col-span-3' : 'lg:col-span-5'} bg-white/70 dark:bg-slate-900/60 rounded-xl sm:rounded-[1rem] p-4 sm:p-8 shadow-sm border border-gray-900 dark:border-gray-300 flex flex-col justify-between transition-colors duration-300`}>
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mb-8">
              {(viewMode !== 'settings' && viewMode !== 'services') && (
                <div className="flex justify-center items-center gap-4 font-black text-[15px] uppercase italic dark:text-gray-300">
                  <button className="p-2 text-[17px] hover:text-blue-500" onClick={() => setCurrentMonth(new Date(year, month - 1))}>‹</button>
                  <span>{year}. {monthNames[month]}</span>
                  <button className="p-2 text-[17px] hover:text-blue-500" onClick={() => setCurrentMonth(new Date(year, month + 1))}>›</button>
                </div>
              )}

              <div ref={tabsMenuRef} className="relative ml-auto">
                <button
                  type="button"
                  onClick={() => setIsTabsMenuOpen(!isTabsMenuOpen)}
                  className="flex items-center justify-center gap-2 rounded-[0.5rem] bg-white-70 dark:bg-gray-900 border border-gray-900 dark:border-white px-4 py-3 text-[13px] font-black uppercase tracking-wider text-gray-700 dark:text-gray-200 shadow-sm transition-all hover:bg-gray-200 dark:hover:bg-gray-700"
                  aria-label="Menü"
                >
                  <span className="text-lg leading-none">☰</span>
                  <span>Menü</span>
                </button>

                {isTabsMenuOpen && (
                  <div className="absolute right-0 top-full z-20 mt-4 w-[220px] rounded-[0.5rem] border border-gray-900 bg-white p-2 shadow-xl dark:border-gray-300 dark:bg-gray-900">
                    {[
                      { key: 'bookings', label: 'FOGLALÁSOK', className: 'text-blue-700 dark:text-blue-500' },
                      { key: 'schedule', label: 'MUNKAIDŐ', className: 'text-amber-600 dark:text-amber-500' },
                      { key: 'closed', label: 'SZÜNET', className: 'text-rose-600 dark:text-rose-400' },
                      { key: 'manual', label: 'TELEFONOS FOGLALÁS', className: 'text-cyan-600 dark:text-cyan-500' },
                      { key: 'settings', label: 'BEÁLLÍTÁSOK', className: 'text-emerald-600 dark:text-emerald-500' },
                      { key: 'services', label: 'SZOLGÁLTATÁSOK', className: 'text-purple-600 dark:text-purple-400' }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setViewMode(tab.key);
                          setIsTabsMenuOpen(false);
                        }}
                        className={`w-full rounded-xl px-3 py-2 text-left text-[9px] sm:text-[10px] font-black transition-all ${viewMode === tab.key ? 'bg-gray-100 shadow dark:bg-gray-800 dark:text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-800'} ${tab.className}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {viewMode === 'manual' ? (
              <div className="space-y-6 sm:space-y-8">
                <div className="p-4 sm:p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl transition-colors">
                  <div className="grid grid-cols-7 gap-1 sm:gap-3 mb-2 text-center">
                    {dayNames.map(day => <div key={day} className="text-[9px] sm:text-[10px] font-black uppercase text-gray-700 dark:text-gray-400">{day}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1 sm:gap-3">
                    {[...Array(offset)].map((_, i) => <div key={`manual-empty-${i}`} />)}
                    {[...Array(daysInMonth)].map((_, i) => {
                      const dayNum = i + 1;
                      const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                      const dateObj = new Date(year, month, dayNum);
                      const isPast = dStr <= todayStr;
                      const isClosed = blockedDates.some(d => d.date === dStr);
                      const hasCustom = customDays.some(d => d.date === dStr);
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      const isUnavailable = isPast || isClosed || (isWeekend && !hasCustom);
                      const bookingCount = allBookings.filter(booking => booking.date === dStr && booking.status !== 'Lemondva').length;

                      let style = 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-emerald-400';
                      if (isPast) style = 'bg-gray-100 dark:bg-gray-900/30 text-gray-300 dark:text-gray-600 border-transparent cursor-not-allowed';
                      else if (isClosed || (isWeekend && !hasCustom)) style = 'bg-rose-50 dark:bg-rose-900/20 text-rose-400 border-rose-100 dark:border-rose-900/30 cursor-not-allowed';
                      else if (selectedAdminDate === dStr) style = 'bg-purple-600 !text-white border-purple-600 ring-2 ring-purple-300 dark:ring-purple-300/80 shadow-lg z-10';
                      else if (hasCustom) style = 'bg-amber-400 dark:bg-amber-500 text-white border-amber-400';
                      else if (bookingCount > 0) style = 'bg-blue-600 text-white border-blue-600';

                      return (
                        <button key={dayNum} type="button" disabled={isUnavailable} onClick={() => setSelectedAdminDate(dStr)} className={`h-12 sm:h-16 rounded-lg sm:rounded-2xl border text-xs sm:text-sm font-black flex flex-col items-center justify-center transition-all ${style} ${selectedAdminDate === dStr ? '!bg-purple-600 !text-white !border-purple-600 ring-2 ring-purple-400 shadow-lg z-10' : ''}`}>
                          <span>{dayNum}</span>
                          {bookingCount > 0 && <span className="text-[7px] sm:text-[9px] mt-0.5 uppercase font-black bg-white/20 px-1 sm:px-2 py-0.5 rounded-full">{bookingCount} fogl.</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2 sm:gap-4 text-[8px] sm:text-[9px] font-black uppercase text-gray-400 dark:text-gray-500 justify-center">
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white border border-gray-200"></span> Szabad</div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-600"></span> Kiválasztva</div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span> Foglalás</div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span> Zárva</div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span> Egyedi munkaidő</div>
                  </div>
                </div>

              <form onSubmit={handleCreateManualBooking} className="p-4 sm:p-8 border-2 border-dashed border-emerald-200 dark:border-emerald-900/50 rounded-3xl transition-colors flex flex-col justify-between">
                <div className="mb-6">
                  <h3 className="text-lg sm:text-xl font-black uppercase italic dark:text-white">Telefonos foglalás</h3>
                  <p className="mt-1 text-xs font-semibold text-gray-400 dark:text-gray-500">Rögzítsd annak a vendégnek az időpontját, aki telefonon jelentkezett.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-sans not-italic">
                  <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Vendég neve
                    <input required type="text" value={manualBooking.name} onChange={(e) => setManualBooking({...manualBooking, name: e.target.value})} className="mt-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm outline-none focus:border-emerald-500" placeholder="Pl. Kovács Anna" />
                  </label>
                  <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Telefonszám
                    <input required type="tel" value={manualBooking.phone} onChange={(e) => setManualBooking({...manualBooking, phone: e.target.value})} className="mt-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm outline-none focus:border-emerald-500" placeholder="Pl. +36 30 123 4567" />
                  </label>
                  <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Kiválasztott dátum
                    <div className="mt-1 border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-xl p-3 w-full font-black text-sm">{selectedAdminDate.replace(/-/g, '. ')}</div>
                  </div>
                  <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase md:col-span-2">Szolgáltatás
                    <select required value={manualBooking.serviceId} onChange={(e) => setManualBooking({...manualBooking, serviceId: e.target.value})} className="mt-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm outline-none focus:border-emerald-500">
                      <option value="">Válassz szolgáltatást</option>
                      {servicesList.map(service => <option key={service.id} value={service.id}>{service.name} - {service.duration || 30} perc</option>)}
                    </select>
                  </label>
                  <div className="md:col-span-2 flex items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border dark:border-gray-700 p-4 rounded-2xl">
                    <div>
                      <p className="font-black text-sm dark:text-white">Mosással kéri?</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={manualBooking.withWashing} onChange={(e) => setManualBooking({...manualBooking, withWashing: e.target.checked})} />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Időpontok</p>
                      {manualBooking.serviceId && <span className="text-[10px] font-bold text-gray-400">{manualAvailableTimes.length} szabad</span>}
                    </div>
                    {!manualBooking.serviceId ? (
                      <p className="text-xs italic text-gray-400">Válassz szolgáltatást az időpontok megjelenítéséhez.</p>
                    ) : manualAvailableTimes.length === 0 ? (
                      <p className="text-xs italic text-gray-400">Nincs szabad időpont ezen a napon.</p>
                    ) : (
                      <div className="space-y-4">
                        {[['DELELŐTT', manualMorningSlots], ['DÉLUTÁN', manualAfternoonSlots]].map(([label, slots]) => slots.length > 0 && (
                          <div key={label}>
                            <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{label} ({slots.length})</p>
                            <div className="grid grid-cols-3 gap-2">
                              {slots.map(time => <button key={time} type="button" onClick={() => setManualBooking({...manualBooking, time})} className={`py-2.5 rounded-xl font-black text-xs border transition-all ${manualBooking.time === time ? 'bg-emerald-500 text-white border-emerald-500 shadow-md' : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-emerald-400'}`}>{time}</button>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button type="submit" disabled={isCreatingManualBooking} className="mt-6 w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-black uppercase text-xs px-8 py-4 rounded-xl shadow-lg">
                  {isCreatingManualBooking ? 'Rögzítés...' : 'Telefonos foglalás rögzítése'}
                </button>
              </form>
              </div>
            ) : viewMode === 'services' ? (
              <div className="p-4 sm:p-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl transition-colors">
                  <h3 ref={servicesSectionRef} className="text-lg sm:text-xl font-black uppercase italic mb-6 dark:text-white">Szolgáltatások Kezelése</h3>
                  

                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 sm:p-6 rounded-2xl mb-8 border dark:border-gray-700">
                      <h4 className="font-black uppercase text-sm mb-4 not-italic text-purple-600 dark:text-purple-400">
                          {editingServiceId ? "Szolgáltatás szerkesztése" : "Új szolgáltatás hozzáadása"}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-sans not-italic mb-6">
                          <div>
                              <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Szolgáltatás neve</label>
                              <input type="text" value={newService.name} onChange={(e) => setNewService({...newService, name: e.target.value})} className="border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm focus:border-purple-500 outline-none" placeholder="Pl. Férfi hajvágás" />
                          </div>
                          <div>
                              <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Ár (Ft)</label>
                              <input type="number" value={newService.price} onChange={(e) => setNewService({...newService, price: e.target.value})} className="border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm focus:border-purple-500 outline-none" placeholder="5000" />
                          </div>
                            <div>
                              <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Időtartam (perc)</label>
                              <IntervalPicker value={newService.duration} onChange={(value) => setNewService({...newService, duration: value})} label="Időtartam (perc)" />
                            </div>
                          <div className="md:col-span-2">
                              <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Rövid leírás (opcionális)</label>
                              <input type="text" value={newService.description} onChange={(e) => setNewService({...newService, description: e.target.value})} className="border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full font-bold text-sm focus:border-purple-500 outline-none" placeholder="Klasszikus gépi és ollós vágás" />
                          </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                          <button onClick={handleSaveService} className={`w-full sm:w-auto text-white font-black uppercase text-xs px-8 py-3 rounded-xl shadow-lg transition-all ${editingServiceId ? 'bg-amber-500' : 'bg-purple-500'}`}>
                              {editingServiceId ? "Mentés" : "Hozzáadás"}
                          </button>
                          {editingServiceId && (
                              <button onClick={handleCancelEdit} className="w-full sm:w-auto bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-black uppercase text-xs px-6 py-3 rounded-xl">
                                  Mégse
                              </button>
                          )}
                      </div>
                  </div>
                  <h4 className="font-black uppercase text-sm mb-4 not-italic dark:text-white">Jelenlegi Szolgáltatások ({servicesList.length})</h4>
                  <div className="space-y-3 font-sans not-italic">
                      {servicesList.length > 0 ? servicesList.map(srv => (
                          <div key={srv.id} className={`flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-gray-100 dark:bg-gray-800 border p-4 rounded-xl shadow-sm ${editingServiceId === srv.id ? 'border-amber-400' : 'border-gray-500 dark:border-gray-700'}`}>
                              <div>
                                  <p className="font-black text-sm sm:text-[15px] dark:text-white">{srv.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{srv.description || 'Nincs leírás'} · {srv.duration || 30} perc</p>
                              </div>
                              <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-3 pt-2 sm:pt-0 border-t sm:border-none dark:border-gray-700">
                                  <span className="font-black text-emerald-600 dark:text-emerald-400 text-base sm:text-lg mr-2">{srv.price} Ft</span>
                                  <div className="flex gap-2">
                                      <button onClick={() => handleEditClick(srv)} className="border border-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase px-3 py-2 rounded-lg">Szerkesztés</button>
                                      <button onClick={() => handleDeleteService(srv.id)} className="border border-red-500 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-black uppercase px-3 py-2 rounded-lg">Törlés</button>
                                  </div>
                              </div>
                          </div>
                      )) : <p className="text-sm text-gray-400 italic">Nincsenek elérhető szolgáltatások.</p>}
                  </div>
              </div>
            ) : viewMode === 'settings' ? (
               <div className="p-4 sm:p-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl not-italic transition-colors">
                  <h3 className="text-lg sm:text-xl font-black uppercase italic mb-6 dark:text-white">Általános beállítások</h3>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-500 dark:border-gray-600 p-4 sm:p-6 rounded-2xl mb-4">
                      <div>
                        <p className="font-black uppercase text-sm dark:text-white">Webhely neve</p>
                        <p className="text-[10px] font-bold opacity-50 dark:text-gray-400">Ez a név jelenik meg a fejlécben és a láblécben</p>
                      </div>
                      <input
                        type="text"
                        value={siteName}
                        onChange={(e) => setSiteNameValue(e.target.value)}
                        maxLength={80}
                        className="border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full sm:w-72 font-black text-base outline-none"
                        placeholder="Fodrász"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-500 dark:border-gray-600 p-4 sm:p-6 rounded-2xl mb-4">
                      <div>
                        <p className="font-black uppercase text-sm dark:text-white">Oldal címe</p>
                        <p className="text-[10px] font-bold opacity-50 dark:text-gray-400">Ez jelenik meg a böngésző lapfülén</p>
                      </div>
                      <input
                        type="text"
                        value={siteTitle}
                        onChange={(e) => setSiteTitleValue(e.target.value)}
                        maxLength={120}
                        className="border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-full sm:w-72 font-black text-base outline-none"
                        placeholder="Fodrász"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-500 dark:border-gray-600 p-4 sm:p-6 rounded-2xl mb-4">
                      <div>
                        <p className="font-black uppercase text-sm dark:text-white">Általános nyitvatartás</p>
                        <p className="text-[10px] font-bold opacity-50 dark:text-gray-400">A normál munkanapok foglalható időszaka</p>
                      </div>
                      <div className="flex w-full flex-col items-stretch gap-2 font-sans not-italic sm:w-auto sm:flex-row sm:items-center">
                        <TimePicker value={openingHours.open} onChange={(value) => setOpeningHours({...openingHours, open: value})} label="Nyitási idő" />
                        <span className="hidden font-black text-gray-400 dark:text-gray-500 sm:inline">-</span>
                        <TimePicker value={openingHours.close} onChange={(value) => setOpeningHours({...openingHours, close: value})} label="Zárási idő" />
                      </div>
                    </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-500 dark:border-gray-600 p-4 sm:p-6 rounded-2xl">
                      <div>
                          <p className="font-black uppercase text-sm dark:text-white">Hajmosás opció ára</p>
                        <p className="text-[10px] font-bold opacity-50 dark:text-gray-400">Mennyivel növelje az alapárat?</p>
                      </div>
                      <div className="flex items-center gap-3 font-sans not-italic w-full sm:w-auto min-w-0">
                          <input type="number" value={washingPrice} onChange={(e) => setWashingPrice(e.target.value)} className="border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white rounded-xl p-3 w-0 flex-1 sm:w-32 sm:flex-none text-center font-black text-base sm:text-lg outline-none" />
                          <span className="shrink-0 font-black text-gray-400 dark:text-gray-500">Ft</span>
                        <label className="relative inline-flex shrink-0 items-center cursor-pointer" title="Hajmosás opció be- vagy kikapcsolása">
                        <input type="checkbox" className="sr-only peer" checked={washingEnabled} onChange={(e) => setWashingEnabled(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                        </label>
                      </div>
                  </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-500 dark:border-gray-600 p-4 sm:p-6 rounded-2xl mt-4">
                      <div>
                        <p className="font-black uppercase text-sm dark:text-white">Foglalási időköz</p>
                        <p className="text-[10px] font-bold opacity-50 dark:text-gray-400">Ennyi percenként jelenjen meg új foglalható időpont</p>
                      </div>
                      <div className="flex items-center gap-3 font-sans not-italic w-full sm:w-auto">
                        <IntervalPicker value={slotInterval} onChange={setSlotInterval} label="Foglalási időköz" />
                      </div>
                    </div>
                  <button onClick={handleSaveSettings} disabled={isSaving} className="mt-8 w-full sm:w-auto bg-emerald-500 text-white font-black uppercase text-xs px-8 py-4 rounded-full shadow-lg">
                      {isSaving ? "Mentés..." : "Beállítások mentése"}
                  </button>
               </div>
            ) : (
              <>
                {viewMode === 'schedule' && (
                  <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between border border-amber-100 dark:border-amber-800/50">
                    <span className="text-[10px] font-black uppercase text-amber-700 dark:text-amber-400 italic">Egyedi Munkaidő:</span>
                    <div className="flex gap-2 font-sans not-italic w-full sm:w-auto">
                      <input type="time" value={workTime.start} onChange={e => setWorkTime({...workTime, start: e.target.value})} className="flex-1 sm:flex-none border dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded p-1 text-xs font-black" />
                      <input type="time" value={workTime.end} onChange={e => setWorkTime({...workTime, end: e.target.value})} className="flex-1 sm:flex-none border dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded p-1 text-xs font-black" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-7 gap-1 sm:gap-3 mb-2 text-center">
                  {dayNames.map(day => <div key={day} className="text-[9px] sm:text-[15px] font-black uppercase text-gray-700 dark:text-gray-400">{day}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-3">
                  {[...Array(offset)].map((_, i) => <div key={`empty-${i}`} />)}
                  {[...Array(daysInMonth)].map((_, i) => {
                    const dayNum = i + 1;
                    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                    const isToday = dStr === todayStr;
                    const dayOfWeek = new Date(year, month, dayNum).getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const isClosed = blockedDates.some(d => d.date === dStr);
                    const custom = customDays.find(d => d.date === dStr);
                    const count = allBookings.filter(b => b.date === dStr).length;
                    
                    let style = "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200";
                    if (isClosed) style = "bg-rose-500 dark:bg-rose-600 text-white border-rose-500";
                    else if (custom) style = "bg-amber-400 dark:bg-amber-500 text-white border-amber-400";
                    else if (count > 0) style = "bg-blue-600 text-white border-blue-600";
                    else if (isWeekend) style = "bg-red-50 dark:bg-red-900/20 text-red-400 border-red-100 dark:border-red-900/30";
                    
                    return (
                      <button key={dayNum} onClick={() => handleDayClick(dayNum)} className={`h-12 sm:h-20 rounded-lg sm:rounded-[0.5rem] border text-xs sm:text-sm font-black flex flex-col items-center justify-center transition-all ${style} ${selectedAdminDate === dStr ? '!bg-purple-600 !text-white ring-2 ring-purple-400 !border-purple-600 shadow-lg sm:scale-105 z-10' : ''} ${isToday ? 'border-blue-400 border-2' : ''}`}>
                        <span>{dayNum}</span>
                        {custom && <span className="text-[9px] sm:text-[11px] font-bold opacity-80 hidden sm:block">{custom.start_time}-{custom.end_time}</span>}
                        {count > 0 && <span className="text-[7px] sm:text-[9px] mt-0.5 sm:mt-1 uppercase font-black bg-white/20 px-1 sm:px-2 py-0.5 rounded-full">{count}fő</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {(viewMode !== 'settings' && viewMode !== 'services') && (
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2 sm:gap-4 text-[8px] sm:text-[9px] font-black uppercase text-gray-400 dark:text-gray-500 justify-center">
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white border border-gray-200"></span> Normál</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-600"></span> Kiválasztva</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 border border-red-400"></span> Hétvége</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span> Foglalás</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span> Zárva</div>
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span> Egyedi</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

//  UI komponens a statisztikákhoz.
function StatCard({ title, value, color }) {
  return (
    <div className={`${color} p-5 sm:p-7 rounded-2xl sm:rounded-[2.5rem] shadow-lg transition-colors duration-300`}>
      <p className="text-xs sm:text-[15px] font-black uppercase mb-1 italic text-white">{title}</p>
      <p className="text-2xl sm:text-3xl font-black italic text-white">{value}</p>
    </div>
  );
}

// Üres állapot a foglalási oldalhoz: ha egy napszakban nincs vendég.
function EmptyScheduleState({ label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 px-3 py-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" aria-hidden="true">
        ✓
      </span>
      <div>
        <p className="text-[10px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">Szabad idősáv</p>
        <p className="mt-0.5 text-xs font-semibold text-gray-400 dark:text-gray-500">{label}</p>
      </div>
    </div>
  );
}

// Egy foglalás sorának renderelése a naptár oldalhoz.
// Státusz alapján színezés
function BookingRow({ apt, onDelete, onComplete, onNoShow }) {
    const isCompleted = apt.status === 'Kész';
    const isCancelled = apt.status === 'Lemondva';
  const isNoShow = apt.status === 'Nem jött';

    const getContainerStyle = () => {
        if (isCancelled) return 'bg-gray-100 dark:bg-gray-800/50 border-red-200 dark:border-red-900/50 opacity-60';
        if (isCompleted) return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/60';
        if (isNoShow) return 'bg-rose-50 dark:bg-rose-900/20 border-rose-300 dark:border-rose-800/60';
        return 'bg-gray-50 dark:bg-gray-800/80 border-blue-500';
    };

    return (
      <div className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-l-4 ${getContainerStyle()} flex justify-between items-center mb-3 shadow-sm italic transition-all group`}>
        <div className="not-italic min-w-0 pr-2">
            <div className="flex items-center gap-1 sm:gap-2">
                <p className="text-base sm:text-lg font-black dark:text-white">{apt.time}</p>
                {isCompleted && <span className="text-[7px] sm:text-[8px] bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-bold uppercase">Kész</span>}
                {isCancelled && <span className="text-[7px] sm:text-[8px] bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded-full font-bold uppercase">Lemondva</span>}
                {isNoShow && <span className="text-[7px] sm:text-[8px] bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-400 px-1.5 py-0.5 rounded-full font-bold uppercase">Nem jött</span>}
            </div>
            <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase truncate">{apt.customer_name || 'Ismeretlen'}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="flex flex-col items-end gap-1">
                <span className="text-[7px] sm:text-[8px] font-black bg-white dark:bg-gray-700 dark:text-gray-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border dark:border-gray-600 uppercase shadow-sm tracking-tighter truncate max-w-[90px] sm:max-w-none">{apt.service_name}</span>
                {apt.with_washing === 1 && <span className="text-[6px] sm:text-[7px] font-black bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded uppercase">Mosással</span>}
            </div>
            <div className="flex gap-1 sm:gap-2">
                {!isCompleted && !isCancelled && (
                  <button onClick={() => onComplete(apt.id)} className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 p-1.5 sm:p-2 rounded-xl" title="Készre jelentés">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                  </button>
                )}
                {!isCompleted && !isCancelled && !isNoShow && (
                  <button onClick={() => onNoShow(apt.id)} className="bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 p-1.5 sm:p-2 rounded-xl" title="Nem jött el">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
                      </svg>
                  </button>
                )}
                <button onClick={() => onDelete(apt.id)} className="bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 p-1.5 sm:p-2 rounded-xl" title="Törlés">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>
        </div>
      </div>
    );
}